import {
  POLICY_ACTIONS,
  POLICY_SCOPES,
  CONDITION_TYPES,
  CODEBASE_ACTIONS,
  CODEBASE_ACTION_PRIORITY,
} from "@/src/constants/permissionConstants.js";
import { standardObjects } from "@/src/constants/objectConstants.js";
import { capitalizeFirstLetter } from "@/src/utils/utils.js";

export const getCRMResources = () => {
  return standardObjects.map((obj) => capitalizeFirstLetter(obj.slug));
};

function scopeToAPI(uiScope) {
  const scopeMap = {
    [POLICY_SCOPES.ALL]: "ALL_RECORDS",
    [POLICY_SCOPES.NONE]: "NONE_OF_THE_RECORDS",
    [POLICY_SCOPES.CONDITIONAL]: "CONDITIONAL",
    [POLICY_SCOPES.OWN]: "USER",
    [POLICY_SCOPES.TEAM]: "TEAM",
  };
  return scopeMap[uiScope] || "NONE_OF_THE_RECORDS";
}

function scopeFromAPI(apiScope) {
  const scopeMap = {
    ALL_RECORDS: POLICY_SCOPES.ALL,
    NONE_OF_THE_RECORDS: POLICY_SCOPES.NONE,
    USER: POLICY_SCOPES.OWN,
    TEAM: POLICY_SCOPES.TEAM,
    CONDITIONAL: POLICY_SCOPES.CONDITIONAL,
    all: POLICY_SCOPES.ALL,
    none: POLICY_SCOPES.NONE,
    own: POLICY_SCOPES.OWN,
    team: POLICY_SCOPES.TEAM,
    conditional: POLICY_SCOPES.CONDITIONAL,
  };
  return scopeMap[apiScope] || POLICY_SCOPES.NONE;
}

function buildConditionPayload(conditionData, objectProperties) {
  if (!conditionData) {
    console.warn("buildConditionPayload: No conditionData provided");
    return null;
  }

  const { type, property, users, teams } = conditionData;

  const attributeIds = (property || [])
    .map((slug) => {
      const attr = objectProperties.find((p) => p.slug === slug);
      if (!attr) {
        console.warn(
          `Could not find attribute for slug: ${slug}. Available:`,
          objectProperties.map((p) => p.slug)
        );
      }
      return attr?.id;
    })
    .filter(Boolean);

  const typeMap = {
    [CONDITION_TYPES.USER_IN_ROLE]: CONDITION_TYPES.USER_SCOPE,
    [CONDITION_TYPES.USER_AND_TEAM]: CONDITION_TYPES.TEAM_SCOPE,
    [CONDITION_TYPES.SPECIFIC_USERS_TEAMS]:
      CONDITION_TYPES.SELECTED_ENTITIES_SCOPE,
  };

  const backendType = typeMap[type];

  if (!backendType) {
    console.warn(
      `Invalid condition type: ${type}. Expected one of:`,
      Object.keys(typeMap)
    );
    return null;
  }

  const entities = [
    ...(users || []).map((id) => ({ type: "USER", entity_id: [id] })),
    ...(teams || []).map((id) => ({ type: "TEAM", entity_id: [id] })),
  ];

  const payload = { type: backendType, entities, attributes: attributeIds };

  return payload;
}

export function parseConditionFromAPI(apiCondition, objectProperties) {
  if (!apiCondition) return null;

  const typeMap = {
    [CONDITION_TYPES.USER_SCOPE]: CONDITION_TYPES.USER_IN_ROLE,
    [CONDITION_TYPES.TEAM_SCOPE]: CONDITION_TYPES.USER_AND_TEAM,
    [CONDITION_TYPES.SELECTED_ENTITIES_SCOPE]:
      CONDITION_TYPES.SPECIFIC_USERS_TEAMS,
  };

  const uiType = typeMap[apiCondition.type];

  if (!uiType) return null;

  const propertySlugs = (apiCondition.attributes || [])
    .map((attrId) => {
      const attr = objectProperties.find((p) => p.id === attrId);
      return attr?.slug;
    })
    .filter(Boolean);

  const result = {
    type: uiType,
    property: propertySlugs,
  };

  if (apiCondition.entities) {
    result.users = apiCondition.entities
      .filter((e) => e.type === "USER")
      .map((e) => (Array.isArray(e.entity_id) ? e.entity_id : [e.entity_id]))
      .flat();
    result.teams = apiCondition.entities
      .filter((e) => e.type === "TEAM")
      .map((e) => (Array.isArray(e.entity_id) ? e.entity_id : [e.entity_id]))
      .flat();
  } else {
    result.users = [];
    result.teams = [];
  }

  return result;
}

export function shouldEnableResource(resourceName, existingPolicies) {
  const resourceLower = resourceName.toLowerCase();
  return existingPolicies.some(
    (p) => p.attributes.resource_id === resourceLower
  );
}

// Returns the highest-privilege codebase action (MANAGE > WRITE > READ) already
// stored as a policy for this resource+role pair, or null if none exists.
export function getCodebaseActionFromPolicies(resourceId, existingPolicies, roleId) {
  const resourceLower = resourceId.toLowerCase();
  for (const action of CODEBASE_ACTION_PRIORITY) {
    const found = existingPolicies.find(
      (p) =>
        p.attributes.resource_id === resourceLower &&
        p.attributes.action.toUpperCase() === action &&
        p.attributes.role_id === roleId
    );
    if (found) return action;
  }
  return null;
}

export function getDefaultFullAccessPermissions() {
  return {
    enabled: true,
    create: true,
    view: POLICY_SCOPES.ALL,
    edit: POLICY_SCOPES.ALL,
    delete: POLICY_SCOPES.ALL,
    export: true,
    import: true,
  };
}

export function getDefaultEmptyPermissions() {
  return {
    enabled: false,
    create: false,
    view: POLICY_SCOPES.NONE,
    edit: POLICY_SCOPES.NONE,
    delete: POLICY_SCOPES.NONE,
    export: false,
    import: false,
  };
}

export function buildSimpleResourcePolicy(roleId, resourceId, enabled) {
  if (!enabled) return null;

  return {
    role_id: roleId,
    resource_id: resourceId.toLowerCase(),
    action: "VIEW",
    scope: "ALL_RECORDS",
  };
}

export function buildPolicyObject(
  roleId,
  resourceId,
  action,
  scopeValue,
  conditionData = null,
  objectProperties = []
) {
  const actionUpper = action.toUpperCase();
  const resourceLower = resourceId.toLowerCase();

  if (
    [
      POLICY_ACTIONS.CREATE,
      POLICY_ACTIONS.EXPORT,
      POLICY_ACTIONS.IMPORT,
    ].includes(actionUpper)
  ) {
    return {
      role_id: roleId,
      resource_id: resourceLower,
      action: actionUpper,
      scope: "ALL_RECORDS",
    };
  }

  const policy = {
    role_id: roleId,
    resource_id: resourceLower,
    action: actionUpper,
    scope: scopeToAPI(scopeValue),
  };

  if (scopeToAPI(scopeValue) === "CONDITIONAL") {
    if (conditionData) {
      const condition = buildConditionPayload(conditionData, objectProperties);
      if (condition) {
        policy.condition = condition;
      } else {
        console.warn(
          `CONDITIONAL scope for ${actionUpper} on ${resourceLower} but condition could not be built. ConditionData:`,
          conditionData
        );
        return null;
      }
    } else {
      console.warn(
        `CONDITIONAL scope for ${actionUpper} on ${resourceLower} but no conditionData provided.`
      );
      return null;
    }
  }

  return policy;
}

export function findExistingPolicy(
  existingPolicies,
  roleId,
  resourceId,
  action
) {
  const resourceLower = resourceId.toLowerCase();
  const actionUpper = action.toUpperCase();

  return existingPolicies.find(
    (p) =>
      p.attributes.role_id === roleId &&
      p.attributes.resource_id === resourceLower &&
      p.attributes.action.toUpperCase() === actionUpper
  );
}

export function shouldDeleteAction(action, currentValue) {
  const actionLower = action.toLowerCase();

  if (
    [
      POLICY_ACTIONS.CREATE.toLowerCase(),
      POLICY_ACTIONS.EXPORT.toLowerCase(),
      POLICY_ACTIONS.IMPORT.toLowerCase(),
    ].includes(actionLower)
  ) {
    return currentValue === false;
  }

  return currentValue === POLICY_SCOPES.NONE || currentValue === "none";
}

export function hasValidScope(action, currentValue) {
  const actionLower = action.toLowerCase();

  if (
    [
      POLICY_ACTIONS.CREATE.toLowerCase(),
      POLICY_ACTIONS.EXPORT.toLowerCase(),
      POLICY_ACTIONS.IMPORT.toLowerCase(),
    ].includes(actionLower)
  ) {
    return currentValue === true;
  }

  return (
    currentValue &&
    currentValue !== POLICY_SCOPES.NONE &&
    currentValue !== "none" &&
    currentValue !== POLICY_SCOPES.CONDITIONAL
  );
}

export function calculatePolicyDiff(
  existingPolicies,
  currentPermissions,
  selectedPermissions,
  roleId,
  allObjectProperties = {}
) {
  const toCreate = [];
  const toUpdate = [];
  const toDelete = [];
  const skippedPolicies = [];

  // Helper function to check if a resource has CRM-style permissions
  const isCRMStylePermission = (permissions) => {
    return (
      permissions &&
      typeof permissions === "object" &&
      ("create" in permissions ||
        "view" in permissions ||
        "edit" in permissions ||
        "delete" in permissions ||
        "export" in permissions ||
        "import" in permissions)
    );
  };

  // Dynamically collect all CRM resource IDs from currentPermissions
  const allCrmResourceIds = new Set();
  Object.keys(currentPermissions).forEach((resourceKey) => {
    if (isCRMStylePermission(currentPermissions[resourceKey])) {
      allCrmResourceIds.add(resourceKey);
    }
  });

  // Process all CRM resources
  allCrmResourceIds.forEach((resourceId) => {
    const current = currentPermissions[resourceId];
    const isEnabled = selectedPermissions[resourceId];

    if (!current) {
      return;
    }

    // For CRM resources, check if ANY action is enabled (not just the main toggle)
    const hasAnyActionEnabled =
      current.create ||
      (current.view && current.view !== "none") ||
      (current.edit && current.edit !== "none") ||
      (current.delete && current.delete !== "none") ||
      current.export ||
      current.import;

    if (!hasAnyActionEnabled) {
      // Delete all policies for this resource if no actions are enabled
      const resourceLower = resourceId.toLowerCase();
      existingPolicies
        .filter((p) => p.attributes.resource_id === resourceLower)
        .forEach((p) => toDelete.push(p.id));
      return;
    }

    const objectProperties = allObjectProperties[resourceId] || [];

    [
      POLICY_ACTIONS.CREATE,
      POLICY_ACTIONS.VIEW,
      POLICY_ACTIONS.EDIT,
      POLICY_ACTIONS.DELETE,
      POLICY_ACTIONS.EXPORT,
      POLICY_ACTIONS.IMPORT,
    ].forEach((action) => {
      const actionLower = action.toLowerCase();
      const currentValue = current[actionLower];
      const existing = findExistingPolicy(
        existingPolicies,
        roleId,
        resourceId,
        action
      );

      if (shouldDeleteAction(actionLower, currentValue)) {
        if (existing) toDelete.push(existing.id);
      } else if (hasValidScope(actionLower, currentValue)) {
        const conditionKey = `${actionLower}_condition`;
        const conditionData = current[conditionKey] || null;

        const policy = buildPolicyObject(
          roleId,
          resourceId,
          action,
          currentValue,
          conditionData,
          objectProperties
        );

        if (!policy) {
          console.warn(
            `Skipping invalid policy for ${resourceId}.${actionLower}`
          );
          skippedPolicies.push({
            resource: resourceId,
            action: actionLower,
            reason: "Invalid policy configuration",
          });
          return;
        }

        if (existing) {
          const existingScope = existing.attributes.scope;
          const existingCondition = existing.attributes.condition || null;

          const existingConditionStr = JSON.stringify(existingCondition);
          const newConditionStr = JSON.stringify(policy.condition || null);

          const hasConditionChanged = existingConditionStr !== newConditionStr;

          if (existingScope !== policy.scope || hasConditionChanged) {
            toUpdate.push({ id: existing.id, policy });
          }
        } else {
          toCreate.push(policy);
        }
      } else if (currentValue === POLICY_SCOPES.CONDITIONAL) {
        const conditionKey = `${actionLower}_condition`;
        const conditionData = current[conditionKey] || null;

        const policy = buildPolicyObject(
          roleId,
          resourceId,
          action,
          currentValue,
          conditionData,
          objectProperties
        );

        if (!policy) {
          console.warn(
            `Skipping invalid CONDITIONAL policy for ${resourceId}.${actionLower}. Please configure condition properly.`
          );
          skippedPolicies.push({
            resource: resourceId,
            action: actionLower,
            reason:
              "CONDITIONAL scope requires complete condition configuration (type and properties)",
          });
          if (existing) {
            toDelete.push(existing.id);
          }
          return;
        }

        if (existing) {
          const existingScope = existing.attributes.scope;
          const existingCondition = existing.attributes.condition || null;

          const existingConditionStr = JSON.stringify(existingCondition);
          const newConditionStr = JSON.stringify(policy.condition || null);

          const hasConditionChanged = existingConditionStr !== newConditionStr;

          if (existingScope !== policy.scope || hasConditionChanged) {
            toUpdate.push({ id: existing.id, policy });
          }
        } else {
          toCreate.push(policy);
        }
      }
    });

    const enabledValue = current.enabled;
    const enabledExisting = findExistingPolicy(
      existingPolicies,
      roleId,
      resourceId,
      POLICY_ACTIONS.ENABLED
    );

    if (enabledValue === false) {
      if (enabledExisting) toDelete.push(enabledExisting.id);
    } else if (enabledValue === true) {
      const policy = {
        role_id: roleId,
        resource_id: resourceId.toLowerCase(),
        action: POLICY_ACTIONS.ENABLED,
        scope: "ALL_RECORDS",
      };

      if (!enabledExisting) {
        toCreate.push(policy);
      }
    }
  });

  // Process non-CRM resources
  Object.keys(selectedPermissions).forEach((resourceKey) => {
    // Skip if it's a CRM resource (already processed above)
    if (allCrmResourceIds.has(resourceKey)) return;

    const isEnabled = selectedPermissions[resourceKey];
    const resourceLower = resourceKey.toLowerCase();


    if (currentPermissions[resourceKey]?.codebaseAction !== undefined) {
      const current = currentPermissions[resourceKey];
      const newAction = current.codebaseAction;
      const codebaseActionValues = Object.values(CODEBASE_ACTIONS);
      const existingCodebasePolicies = existingPolicies.filter(
        (p) =>
          codebaseActionValues.includes(p.attributes.action.toUpperCase()) &&
          p.attributes.resource_id === resourceLower &&
          p.attributes.role_id === roleId
      );

      if (!isEnabled || !newAction) {
        existingCodebasePolicies.forEach((p) => toDelete.push(p.id));
        return;
      }

      const policy = {
        role_id: roleId,
        resource_id: resourceLower,
        action: newAction,
        scope: "ALL_RECORDS",
      };

      const exactMatch = existingCodebasePolicies.find(
        (p) => p.attributes.action.toUpperCase() === newAction
      );
      const stale = existingCodebasePolicies.filter(
        (p) => p.attributes.action.toUpperCase() !== newAction
      );
      stale.forEach((p) => toDelete.push(p.id));
      if (!exactMatch) {
        toCreate.push(policy);
      }
      return;
    }

    if (!isEnabled) {
      existingPolicies
        .filter((p) => p.attributes.resource_id === resourceLower)
        .forEach((p) => toDelete.push(p.id));
      return;
    }

    const current = currentPermissions[resourceKey];
    if (!current) return;

    const isSimpleResource =
      !current.view && !current.edit && current.enabled !== undefined;

    if (isSimpleResource && current.enabled) {
      // Simple resources use ENABLED action (not VIEW)
      const existing = existingPolicies.find(
        (p) =>
          p.attributes.resource_id === resourceLower &&
          (p.attributes.action === POLICY_ACTIONS.ENABLED ||
            p.attributes.action === POLICY_ACTIONS.VIEW) &&
          p.attributes.role_id === roleId
      );

      const policy = {
        role_id: roleId,
        resource_id: resourceLower,
        action: POLICY_ACTIONS.ENABLED,
        scope: "ALL_RECORDS",
      };

      if (existing) {
        if (
          existing.attributes.scope !== "ALL_RECORDS" ||
          existing.attributes.action !== POLICY_ACTIONS.ENABLED
        ) {
          toUpdate.push({ id: existing.id, policy });
        }
      } else {
        toCreate.push(policy);
      }
    } else if (current.view || current.edit || current.delete) {
      const hasAnyAccess =
        (current.view &&
          current.view !== POLICY_SCOPES.NONE &&
          current.view !== "none") ||
        (current.edit &&
          current.edit !== POLICY_SCOPES.NONE &&
          current.edit !== "none") ||
        (current.delete &&
          current.delete !== POLICY_SCOPES.NONE &&
          current.delete !== "none");

      if (hasAnyAccess) {
        // Non-CRM resources with CRM-style structure use ENABLED action
        const existing = existingPolicies.find(
          (p) =>
            p.attributes.resource_id === resourceLower &&
            (p.attributes.action === POLICY_ACTIONS.ENABLED ||
              p.attributes.action === POLICY_ACTIONS.VIEW) &&
            p.attributes.role_id === roleId
        );

        const policy = {
          role_id: roleId,
          resource_id: resourceLower,
          action: POLICY_ACTIONS.ENABLED,
          scope: "ALL_RECORDS",
        };

        if (existing) {
          if (
            existing.attributes.scope !== "ALL_RECORDS" ||
            existing.attributes.action !== POLICY_ACTIONS.ENABLED
          ) {
            toUpdate.push({ id: existing.id, policy });
          }
        } else {
          toCreate.push(policy);
        }
      }
    }
  });

  if (skippedPolicies.length > 0) {
    console.warn(
      "Some policies were skipped due to invalid configuration:",
      skippedPolicies
    );
  }

  return { toCreate, toUpdate, toDelete, skippedPolicies };
}

export function transformFromPolicies(policies, allObjectProperties = {}) {
  const extraPermissions = {};

  const policiesByResource = policies.reduce((acc, policy) => {
    const resource = policy.resource; // Keep resource ID as-is (don't capitalize UUIDs)
    if (!acc[resource]) acc[resource] = [];
    acc[resource].push(policy);
    return acc;
  }, {});

  Object.entries(policiesByResource).forEach(([resource, resourcePolicies]) => {
    const permissions = {
      enabled: false,
      create: false,
      view: POLICY_SCOPES.NONE,
      edit: POLICY_SCOPES.NONE,
      delete: POLICY_SCOPES.NONE,
      export: false,
      import: false,
    };

    const resourceLower = resource.toLowerCase();
    const objectProperties =
      allObjectProperties[resourceLower] || allObjectProperties[resource] || [];

    resourcePolicies.forEach((policy) => {
      const action = policy.action.toUpperCase();
      const actionLower = action.toLowerCase();

      switch (action) {
        case "ENABLED":
          permissions.enabled = true;
          break;
        case "CREATE":
          permissions.create = true;
          break;
        case "VIEW":
          permissions.view = scopeFromAPI(policy.scope);
          if (policy.scope === "CONDITIONAL") {
            if (policy.condition) {
              permissions.view_condition = parseConditionFromAPI(
                policy.condition,
                objectProperties
              );
            } else {
              console.warn(
                `Policy for VIEW on ${resource} has CONDITIONAL scope but no condition. Initializing with default.`
              );
              permissions.view_condition = {
                type: "user_in_role",
                property: [],
              };
            }
          }
          break;
        case "EDIT":
          permissions.edit = scopeFromAPI(policy.scope);
          if (policy.scope === "CONDITIONAL") {
            if (policy.condition) {
              permissions.edit_condition = parseConditionFromAPI(
                policy.condition,
                objectProperties
              );
            } else {
              console.warn(
                `Policy for EDIT on ${resource} has CONDITIONAL scope but no condition. Initializing with default.`
              );
              permissions.edit_condition = {
                type: "user_in_role",
                property: [],
              };
            }
          }
          break;
        case "DELETE":
          permissions.delete = scopeFromAPI(policy.scope);
          if (policy.scope === "CONDITIONAL") {
            if (policy.condition) {
              permissions.delete_condition = parseConditionFromAPI(
                policy.condition,
                objectProperties
              );
            } else {
              console.warn(
                `Policy for DELETE on ${resource} has CONDITIONAL scope but no condition. Initializing with default.`
              );
              permissions.delete_condition = {
                type: "user_in_role",
                property: [],
              };
            }
          }
          break;
        case "EXPORT":
          permissions.export = true;
          break;
        case "IMPORT":
          permissions.import = true;
          break;
      }
    });

    extraPermissions[resource] = permissions;
  });

  return extraPermissions;
}
